// © Microsoft Corporation. All rights reserved.
import { AudienceModeType, DefaultInfopaneCountValue, WPOBaseDomainForOneServiceCluster, WPOIntEnableKey, WPOUseBaseDomainQSOverride, WpoFeedUrlPath, WpoIntServiceFeedBaseDomain, WpoIntServiceMyfeedPath, WpoOneServiceDhpPageId, WpoOneServiceFeedBaseDomain, WpoOneServiceNtpPageId, WpoOneServicePagePath, WpoOneServiceXFeedPageId, WpoQueryStringKeys, apiKeyForChildWpo, initBaseRequestParameters4Wpo, ocidForChildMode } from "@msnews/experiences-constants";
import { DesktopFeedEdgeNextConfigIsNull, WpoFeedFetchFailed, WpoFeedGetMuidFailure, WpoFeedImageFetchFailed } from "../AppErrors";
import { ExperienceTypes, UrlSearchParamsHelper, WebWorkerMessageId, environment, logger, registerCommandMessageHandler, scnMapping, sendMessage, webWorkerCache } from "../wwCommon";
import { addEndPerfMarker, addStartPerfMarker, tryReformatGuid, trySetSearchParam } from "./Common";
import { experienceConfigMap, getExperienceTypeConfig } from "../wwResolver";
import { OneServiceMetadataType } from "./Constants";
import { fetchImage } from "./Image";
import { getOneServiceFeedCallHeaders } from "./OneServiceRequest";
import { trimEnterpriseFlightsIfNecessary } from "@msnews/wpo-enterprise";
let feedDisplaySetting;
let renderSingleColumn;
export function registerWpoFeedTasksForEdgeChromium() {
    if (!environment.isPrefetchEnabled) {
        return;
    }
    /**
     * When web worker receives a message, it will use its default handler to send message back to foreground
     * This behavior will make foreground message queue cleared
     * so if we want to wait for real message (message carrys payload) from web worker, we set default message handler in web-worker to no-op
     * P.s. since we do not want to wait for message from web-worker in current design, we comment below sentence
     */
    // registerDataMessageHandler(WebWorkerMessageId.MyFeed, () => {});
    registerCommandMessageHandler(WebWorkerMessageId.UpdateMyFeed, updateWpoFeed);
    registerCommandMessageHandler(WebWorkerMessageId.RenderSingleColumn, (value) => {
        webWorkerCache.set(WebWorkerMessageId.RenderSingleColumn, value);
    });
    // Message to wait for before subscribeToFeedConfigs
    // Index = 0 : FeedDisplaySetting from PSL
    // Index = 1 : Render single column from PSL
    const messagesToWait = [WebWorkerMessageId.FeedDisplaySetting, WebWorkerMessageId.RenderSingleColumn];
    const subscription = webWorkerCache.subscribeMultiple(messagesToWait, (data) => {
        subscription.unsubscribe();
        subscribeToFeedConfigs(data);
    });
}
function updateWpoFeed(cacheBypassValue) {
    webWorkerCache.delete(WebWorkerMessageId.MyFeed);
    fetchRiverFeed(cacheBypassValue);
}
function subscribeToFeedConfigs(data) {
    if (data.length) {
        feedDisplaySetting = data[0];
        renderSingleColumn = data[1];
    }
    const desktopFeedEdgeNextConfigSubscription = experienceConfigMap.subscribe(ExperienceTypes.DesktopFeedEdgeNext, () => {
        desktopFeedEdgeNextConfigSubscription.unsubscribe();
        fetchRiverFeed(environment.cacheBypassValue);
    });
}
function fetchRiverFeed(cacheBypassValue) {
    if (feedDisplaySetting !== "always" /* always */ && feedDisplaySetting !== "peek" /* peek */) {
        return;
    }
    const feedExperience = environment.currentFlights.includes("prg-wpo-singcol2") || renderSingleColumn ? ExperienceTypes.DesktopFeedSingleColumn : ExperienceTypes.DesktopFeedEdgeNext;
    const feedConfig = getExperienceTypeConfig(feedExperience);
    if (!feedConfig) {
        logger.error(Object.assign(Object.assign({}, DesktopFeedEdgeNextConfigIsNull), { message: `fetchOneService-${WebWorkerMessageId.MyFeed}: DesktopFeedEdgeNext config is null when it is expecting to be not null.` }));
        return;
    }
    fetchWpoFeed(feedConfig, WebWorkerMessageId.MyFeed, cacheBypassValue);
}
/**
 * Make fetch call for Wpo my feed and mark cache state as fetching item
 * @param feedConfig - the feed configuration to use for generating the url
 * @param messageId - type of feed, currently only use MyFeed
 * @param cacheBypassValue - value used (if not null) to bypass http cache on request
 */
function fetchWpoFeed(feedConfig, messageId, cacheBypassValue) {
    var _a, _b;
    addStartPerfMarker(messageId);
    const defaultInitialRequest = {
        contentType: "article,video,slideshow,webcontent",
        feedName: "default",
        ocid: "anaheim-ntp-feeds",
        start: 0,
        timeoutMs: 1000,
        useOneServiceUrl: false
    };
    const initialRequestFromConfig = (_b = (_a = feedConfig === null || feedConfig === void 0 ? void 0 : feedConfig.properties) === null || _a === void 0 ? void 0 : _a.riverSectionCardProviderConfig) === null || _b === void 0 ? void 0 : _b.initialRequest;
    const initialRequest = Object.assign(Object.assign({}, defaultInitialRequest), initialRequestFromConfig);
    const url = getUrl(initialRequest, cacheBypassValue);
    if (!url) {
        onFetchComplete(messageId);
        return;
    }
    const headers = getOneServiceFeedCallHeaders();
    if (initialRequest.useOneServiceUrl && !environment.userId) {
        headers.MUID = environment.activityId;
    }
    const init = {
        credentials: "include",
        method: "GET",
        headers
    };
    return fetch(url, init)
        .then((response) => {
        if (!response.ok) {
            throw `WpoFeed call for ${messageId} failed with status code ${response.status}`;
        }
        // Since we did not use sessionId, remove it
        // if (response.headers) {
        //     sessionId = response.headers.get("ddd-session-id");
        // }
        return response.json();
    })
        .then((data) => {
        onFetchComplete(messageId, data);
    })
        .catch((reason) => {
        logger.error(Object.assign(Object.assign({}, WpoFeedFetchFailed), { message: "Wpo MyFeed fetch failed", pb: Object.assign(Object.assign({}, WpoFeedFetchFailed.pb), { customMessage: `Error: ${reason}. Url: ${url}` }) }));
        onFetchComplete(messageId);
    });
}
function logMuidError(customMessage) {
    logger.error(Object.assign(Object.assign({}, WpoFeedGetMuidFailure), { message: "Cannot get MUID.", pb: Object.assign(Object.assign({}, WpoFeedGetMuidFailure.pb), { customMessage: `${customMessage}; ProbableCause: cannot access cookie` }) }));
}
function getUrl(initialRequest, cacheBypassValue) {
    var _a;
    const urlSearchParamsHelper = new UrlSearchParamsHelper();
    initBaseRequestParameters4Wpo(initialRequest, urlSearchParamsHelper);
    if (cacheBypassValue > 0) {
        urlSearchParamsHelper.set("cbypass", cacheBypassValue.toString());
    }
    if (!environment.sendFeedCallActivityIdInHeader) {
        trySetSearchParam(urlSearchParamsHelper, WpoQueryStringKeys.activityId, tryReformatGuid(environment.activityId));
    }
    trySetSearchParam(urlSearchParamsHelper, WpoQueryStringKeys.market, environment.locale);
    const trimmedFlights = (_a = trimEnterpriseFlightsIfNecessary(environment.currentFlights.split(","), environment.audienceMode)) === null || _a === void 0 ? void 0 : _a.join();
    trySetSearchParam(urlSearchParamsHelper, WpoQueryStringKeys.fdhead, trimmedFlights);
    const { useOneServiceUrl, renderSingleColumn, powerRightRailWithWpo, wpoPageId, wpoSchema } = initialRequest;
    // Option-b: flights prg-webcomp-os
    if (useOneServiceUrl) {
        if (!urlSearchParamsHelper.get("User")) {
            trySetSearchParam(urlSearchParamsHelper, "User", environment.userId);
            urlSearchParamsHelper.delete(WpoQueryStringKeys.user);
            if (environment.userId == null) {
                logMuidError("Using OneServiceUrl");
            }
        }
    }
    else {
        trySetSearchParam(urlSearchParamsHelper, WpoQueryStringKeys.user, environment.userId);
        if (environment.userId == null) {
            logMuidError("Not using OneServiceUrl");
        }
    }
    if (initialRequest.feedId) {
        const interestIdParamKey = useOneServiceUrl ? WpoQueryStringKeys.interestIds : WpoQueryStringKeys.ids;
        trySetSearchParam(urlSearchParamsHelper, interestIdParamKey, initialRequest.feedId);
    }
    if (environment.signedIn) {
        trySetSearchParam(urlSearchParamsHelper, WpoQueryStringKeys.signInCookieName, scnMapping[environment.pagetype] || "ANON");
    }
    let pathname = WpoFeedUrlPath;
    let oneServiceFeedPath;
    if (useOneServiceUrl || renderSingleColumn) {
        const wpoPageId = initialRequest.shouldUseNewWpoEndpoint ? WpoOneServiceNtpPageId : WpoOneServiceDhpPageId;
        oneServiceFeedPath = initialRequest.feedId ? `${WpoOneServicePagePath}${WpoOneServiceXFeedPageId}` : `${WpoOneServicePagePath}${wpoPageId}`;
        pathname = oneServiceFeedPath;
    }
    const domain = useOneServiceUrl || renderSingleColumn ? WpoOneServiceFeedBaseDomain : WPOBaseDomainForOneServiceCluster;
    let feedUrl;
    const urlSearchParams = environment.getSearchParams();
    if (urlSearchParams[WPOUseBaseDomainQSOverride] === "enabled") {
        feedUrl = new URL(pathname, environment.configServiceBaseUrl.origin);
    }
    else if (["enabled", "true"].includes(urlSearchParams[WPOIntEnableKey])) {
        feedUrl = new URL(WpoIntServiceMyfeedPath, WpoIntServiceFeedBaseDomain);
    }
    else {
        feedUrl = new URL(pathname, domain);
    }
    // For Flight 'prg-webcomp-os'
    if (useOneServiceUrl || renderSingleColumn) {
        if (!urlSearchParamsHelper.get("newsTop")) {
            urlSearchParamsHelper.set("newsTop", "48");
        }
        if (!urlSearchParamsHelper.get("newsSkip")) {
            urlSearchParamsHelper.set("newsSkip", "0");
        }
        if (!initialRequest.feedId && !urlSearchParamsHelper.get(WpoQueryStringKeys.infopaneCount)) {
            const ipValue = initialRequest.infopaneCount || DefaultInfopaneCountValue;
            urlSearchParamsHelper.set(WpoQueryStringKeys.infopaneCount, ipValue.toString());
        }
        urlSearchParamsHelper.delete(WpoQueryStringKeys.wrapOData);
        urlSearchParamsHelper.delete(WpoQueryStringKeys.disableTypeSerialization);
    }
    if (environment.audienceMode === AudienceModeType.Kids) {
        urlSearchParamsHelper.set(WpoQueryStringKeys.query, AudienceModeType.Kids);
        urlSearchParamsHelper.set(WpoQueryStringKeys.apiKey, apiKeyForChildWpo);
        urlSearchParamsHelper.set(WpoQueryStringKeys.ocid, ocidForChildMode);
    }
    urlSearchParamsHelper.set(WpoQueryStringKeys.audienceMode, environment.audienceMode);
    if (renderSingleColumn) {
        urlSearchParamsHelper.set(WpoQueryStringKeys.wpoSchema, "byregion");
        urlSearchParamsHelper.set(WpoQueryStringKeys.wpoPageId, "singlecol");
    }
    if (powerRightRailWithWpo) {
        urlSearchParamsHelper.set(WpoQueryStringKeys.wpoSchema, wpoSchema);
        urlSearchParamsHelper.set(WpoQueryStringKeys.wpoPageId, wpoPageId);
    }
    const wpoitems = urlSearchParams[WpoQueryStringKeys.wpoitems];
    if (wpoitems && useOneServiceUrl && !initialRequest.feedId) {
        urlSearchParamsHelper.set(WpoQueryStringKeys.wpoitems, wpoitems.toString());
    }
    const bypassTrigger = urlSearchParams[WpoQueryStringKeys.bypassTrigger];
    if (bypassTrigger && (useOneServiceUrl || renderSingleColumn) && !initialRequest.feedId) {
        urlSearchParamsHelper.set(WpoQueryStringKeys.bypassTrigger, bypassTrigger.toString());
    }
    if (initialRequest === null || initialRequest === void 0 ? void 0 : initialRequest.feedName) {
        urlSearchParamsHelper.set(WpoQueryStringKeys.query, initialRequest.feedName);
    }
    feedUrl.search = urlSearchParamsHelper.toString();
    return feedUrl.toString();
}
/**
 * Marks cache state complete for OneService my feed. Responds to any pending messages. And triggers the content preview fetch.
 * @param response - The response
 */
function onFetchComplete(messageId, response = null) {
    addEndPerfMarker(messageId);
    webWorkerCache.set(messageId, { payload: response });
    sendMessage({ id: messageId, fetched: true, payload: response });
    if (response && response.sections && response.sections.length) {
        // Load the first infopane image and the following 3 content card images
        const maxPreloadCount = 4;
        const imagesToPrefetch = [];
        for (const aboveTheFoldCard of response.sections[0].cards) {
            if (aboveTheFoldCard.type === OneServiceMetadataType.Infopane) {
                const firstInfopaneCard = aboveTheFoldCard.subCards[0];
                if (firstInfopaneCard.images && firstInfopaneCard.images[0]) {
                    imagesToPrefetch.push({ metadata: firstInfopaneCard.images[0], size: { width: 612, height: 304 } });
                }
            }
            else if (aboveTheFoldCard.images && aboveTheFoldCard.images[0]) {
                imagesToPrefetch.push({ metadata: aboveTheFoldCard.images[0], size: { width: 300, height: 156 } });
            }
            // If we now have the max number of images to preload, bail.
            if (imagesToPrefetch.length >= maxPreloadCount) {
                break;
            }
        }
        for (const imageToPrefetch of imagesToPrefetch) {
            fetchImage(imageToPrefetch.metadata, imageToPrefetch.size, { appError: WpoFeedImageFetchFailed });
        }
    }
}
/**
 * Test only exports
 */
export const _getUrl = getUrl;
//# sourceMappingURL=WpoFeed.js.map